import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import { Invoice, InvoicesGroups } from '../model/invoices/invoices';
import { useAuth } from '../services/AuthService';
import { InvoiceService } from '../services/InvoicesService';
import { useLogger } from '../services/LogService';
import { AppContext } from '../state/AppContextProvider';
import { useCurrentHouse } from './HouseHooks';

/**
 * Hook useInvoicesList()
 * Fetch events and return it a local state
 */
export const useInvoices = () => {
  const { state } = useContext(AppContext);

  const logger = useLogger('useInvoices');

  const auth = useAuth();

  return {
    invoiceService: InvoiceService.getInstance(
      logger,
      auth,
      state.user?.invoice_url ?? ''
    ),
  };
};

export const useInvoicesList = () => {
  const { state } = useContext(AppContext);

  const { invoiceService } = useInvoices();

  const [invoices, setInvoices] = useState<InvoicesGroups>(groupByYear([]));

  const [isLoading, setLoading] = useState<boolean>(false);

  const logger = useLogger('useInvoicesList');

  useEffect(() => {
    if (state.isConnected) {
      logger.info('getInvoices');
      setLoading(true);
      invoiceService
        .getInvoices()
        .then((invoices) => {
          setInvoices(sortByMonths(invoices));
        })
        .finally(() => {
          setLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user]);

  const sortByMonths = (invoices: Invoice[]): InvoicesGroups => {
    // Sort by months
    invoices.sort((a, b) => {
      return moment(b.issueDate, 'YYYY-MM').diff(
        moment(a.issueDate, 'YYYY-MM')
      );
    });

    // Sort invoices by year
    return groupByYear(invoices);
  };

  function groupByYear(invoices: Invoice[]): InvoicesGroups {
    return invoices.reduce(function (prev, current) {
      var key = current['issueDate'].split('-')[0];
      if (!prev[key]) {
        prev[key] = [];
      }
      prev[key].push(current);
      return prev;
    }, {});
  }

  return {
    invoices: invoices,
    isLoading: isLoading,
  };
};

export const useShowInvoices = () => {
  const { state } = useContext(AppContext);
  const house = useCurrentHouse();

  return (state.user?.invoice_url !== '' && house?.acf?.invoices) ?? false;
};

export const useServiceFilter = () => {
  const showInvoices = useShowInvoices();

  const serviceFilter = (service) =>
    service.slug !== 'invoices' || showInvoices;

  return serviceFilter;
};
